import React from 'react'
import { Grid, Theme, Typography } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import { Modifications as ModificationsType } from '@src/types/graphql-types'
import FeasibilityMessage from './FeasibilityMessage'
import BaseModificationSingle from './BaseModificationSingle'
import {
  InsertModificationArgs,
  shouldRenderBaseModificationMessage,
  shouldRenderFeasibilityMessage,
  useProductFromQueryParam,
} from '../../ProductsConfiguration'
import { BiTubeProduct } from '../../../BiTube/enum'
import { mergeUridineInosineAndCategories } from './BaseModificationHelper'
import messages from '@src/routes/Configurators/messages'

interface BaseModificationsProps {
  disabled: boolean
  degenerateBasesData?: ModificationsType
  insertModificationIntoSequence: ({
    value,
    resetMod,
    isAntiSense,
  }: InsertModificationArgs) => Promise<string>
  isLNABaseEnable: boolean
  isAntiSense?: boolean
}
const useStyle = makeStyles((theme: Theme) => {
  return {
    baseCategoryTitle: {
      fontSize: theme.typography.pxToRem(16),
    },
    infoText: {
      fontSize: theme.typography.pxToRem(14),
    },
  }
})

const BaseModifications: React.FC<BaseModificationsProps> = ({
  disabled,
  degenerateBasesData,
  insertModificationIntoSequence,
  isLNABaseEnable,
  isAntiSense,
}) => {
  const classes = useStyle()
  const intl = useIntl()
  const baseKeyMap = {
    _PC_SPEC_DNA_Degenerate_Bases: 'CONF_DNA_DEGENERATE_BASES',
    _PC_SPEC_UandI: 'CONF_URACLI_INOSINE',
    _PC_SPEC_LNA_BASE: 'CONF_LNA_BASE',
    '_PC_SPEC_2-OMe-RNA-Bases': 'CONF_OME_RNA_BASES',
  }
  const checkForDisabled = (category?: string) => {
    return disabled || (category === '_PC_SPEC_LNA_BASE' && !isLNABaseEnable)
  }
  const product = useProductFromQueryParam()
  const renderBaseModificationMessage =
    shouldRenderBaseModificationMessage(product)
  const renderFeasibilityMessage = shouldRenderFeasibilityMessage(product)

  const { LightCycler } = BiTubeProduct
  const processedDegenerateBasesData =
    product === LightCycler
      ? mergeUridineInosineAndCategories(degenerateBasesData)
      : degenerateBasesData

  const sortedCategories = processedDegenerateBasesData?.category
    ? [...processedDegenerateBasesData.category].sort(
        (a, b) =>
          (a.order ? parseInt(a.order) : 0) - (b.order ? parseInt(b.order) : 0)
      )
    : []

  return (
    <>
      {renderBaseModificationMessage && (
        <Grid container style={{ marginTop: '24px' }}>
          <Typography
            component="span"
            variant="body1"
            data-scrub
            className={classes.infoText}
          >
            <FormattedMessage
              id="CONF_BASE_MODIFICATIONS_MESSAGE"
              defaultMessage="Insert your cursor at the desired position within the sequence field above and click the ‘Add’ link below."
            />
          </Typography>
        </Grid>
      )}
      <Grid
        container
        style={
          renderBaseModificationMessage
            ? { marginTop: '16px' }
            : { marginTop: '5px' }
        }
        spacing={10}
      >
        {sortedCategories.map((category) => (
          <Grid item xs={3} key={category.categoryName}>
            <Typography
              component="span"
              variant="h3"
              data-scrub
              className={classes.baseCategoryTitle}
            >
              {category.categoryDisplayName &&
              baseKeyMap[category.categoryDisplayName]
                ? intl.formatMessage({
                    id: baseKeyMap[category.categoryDisplayName],
                    defaultMessage: category.categoryName,
                  })
                : category.categoryDisplayName &&
                    messages[category.categoryDisplayName]
                  ? intl.formatMessage({
                      id: messages[category.categoryDisplayName]?.id,
                      defaultMessage:
                        messages[category.categoryDisplayName]?.defaultMessage,
                    })
                  : category.categoryName}
            </Typography>
            {category.modifications.map((single) => (
              <BaseModificationSingle
                key={single.name}
                disabled={checkForDisabled(category.categoryDisplayName || '')}
                degenerateBase={single}
                insertModificationIntoSequence={insertModificationIntoSequence}
                isAntiSense={isAntiSense}
              />
            ))}
          </Grid>
        ))}
      </Grid>
      {renderFeasibilityMessage && <FeasibilityMessage />}
    </>
  )
}

export default BaseModifications
